import type {
  AgentHost,
  DeviceProperties,
  DeviceUser,
  FileEntry,
  InstalledApp,
  LogLevel,
  StorageInfo,
} from '@runtime/agent.js';

import {
  collectDeviceProperties,
  getStorageInfo as getBrowserStorageInfo,
} from './device-properties.js';
import { assertHttpUrl } from './url-safety.js';

/**
 * AgentHost backed by the SOTI MobiControl Android Agent JavaScript
 * API. Versions here (v1.x) are the *JS API* version, which is tracked
 * independently of the MobiControl product release (e.g. MobiControl
 * 2025) — don't conflate the two. Selected at boot when:
 *   - `config.mdm === 'mobicontrol'` (customer told us this fleet
 *     runs under MobiControl), AND
 *   - `window.mobicontrol` is actually present (the Lockdown WebView
 *     has injected the agent globals).
 *
 * Both checks matter. If the config says MobiControl but the global
 * isn't injected (preview, dev, misconfigured), we fall through to
 * AndroidIntentAgentHost or BrowserAgentHost instead of erroring on
 * every tile tap.
 *
 * API reference: https://soti.net/mc/help/javascriptapi/en/
 *
 * What this host wires up:
 *   AgentHost method          → SOTI call
 *   launchApp(pkg)            → mobicontrol.app.start(pkg)
 *   openUrl(url, external)    → mobicontrol.android.startActivity(intent)
 *                               when external, window.location.href otherwise
 *   reboot()                  → mobicontrol.device.reboot()
 *   clearAppData(pkg)         → mobicontrol.app.clearData(pkg)
 *   syncNow()                 → mobicontrol.agent.checkIn()
 *   runScript(ref)            → not supported by SOTI's public JS API;
 *                               rejects with a clear message. Use a
 *                               server-side scripted MobiControl profile.
 *
 *   getDeviceProperties()     → layers SOTI identity (manufacturer,
 *                               model, serial, IMEI, SSID, signal,
 *                               battery, carrier, roaming, VPN, MAC,
 *                               bandwidth) over the browser-derived
 *                               UA/screen info.
 *   getInstalledApps()        → mobicontrol.app.installedApps (falls back
 *                               to mobicontrol.packages if present)
 *   getStorageInfo()          → mobicontrol.storage.{internal,external}
 *                               + mobicontrol.memory
 *   readFile/writeFile/listDir → mobicontrol.io File surface
 *   log(level, msg)           → mobicontrol.log.{debug,info,warn,error}
 *   getCurrentUser()          → mobicontrol.sharedDevice.currentUser
 *   getSystemProperty(name)   → mobicontrol.device.getSystemProperty(name)
 *
 * The bindings added beyond the original tile-activation set are built
 * to the documented v1.10 surface but NOT yet validated on an enrolled
 * device — the doc names members without nailing down exact return
 * shapes, so each accessor is defensive and tolerant. Expect minor
 * shape fixups once a MobiControl-enrolled device is in hand.
 *
 * Methods we deliberately do NOT expose at this layer (security /
 * blast-radius concerns; would need an explicit AgentAction extension):
 *   device.factoryReset, device.lock, device.wipeWorkProfile,
 *   agent.restart, app.install/uninstall, agent.enter{Admin,User}Mode.
 */

/* ───── SOTI v1.10 surface — minimum subset we touch ───── */

/** One entry of mobicontrol.app.installedApps. Field names vary across
 *  agent versions; we read several aliases and normalize. */
interface SotiInstalledApp {
  packageName?: string | null;
  name?: string | null;
  label?: string | null;
  versionName?: string | null;
  version?: string | null;
  versionCode?: number | null;
  isSystemApp?: boolean | null;
  isSystem?: boolean | null;
}

interface SotiApp {
  start?: (packageName: string, className?: string) => boolean;
  clearData?: (packageName: string) => void;
  installedApps?: SotiInstalledApp[] | null;
  getInstalledApp?: (packageName: string) => SotiInstalledApp | null;
}

/** mobicontrol.packages — documented as the install interface; some agent
 *  builds also surface an inventory list here, so we tolerate it as a
 *  fallback source for getInstalledApps(). */
interface SotiPackages {
  installedApps?: SotiInstalledApp[] | null;
}

interface SotiDevice {
  id?: string;
  name?: string;
  serialNumber?: string | null;
  manufacturer?: string | null;
  model?: string | null;
  macAddress?: string | null;
  isAirplaneMode?: boolean | null;
  reboot?: () => boolean;
  getSystemProperty?: (name: string) => string | null;
}

interface SotiBattery {
  isCharging?: boolean | null;
  level?: number | null;
  temperature?: number | null;
  voltage?: number | null;
}

interface SotiCellular {
  carrier?: string | null;
  imei?: string | null;
  isRoaming?: boolean | null;
  signalStrength?: number | null;
  signalStrengthInAsu?: number | null;
  getImei?: (slotIndex: number) => string;
}

interface SotiNetworkTransport {
  hasWiFiTransport?: boolean;
  hasCellularTransport?: boolean;
  hasEthernetTransport?: boolean;
  hasBluetoothTransport?: boolean;
  hasVpnTransport?: boolean;
  signalStrength?: number;
  downstreamBandwidthKbps?: number;
  upstreamBandwidthKbps?: number;
  isMetered?: boolean;
}

interface SotiNetwork {
  ssid?: string | null;
  activeNetwork?: SotiNetworkTransport | null;
}

interface SotiAgent {
  checkIn?: () => void;
  getCustomAttribute?: (key: string) => string | null;
  isAdminMode?: () => boolean;
}

/**
 * SOTI Intent builder. Per the JS API intents tutorial, intents use a
 * fluent `with*` builder (each returns the intent for chaining), NOT
 * Android-style setAction/putExtra setters.
 * Ref: https://soti.net/mc/help/javascriptapi/en/tutorial-intents.html
 */
interface SotiIntent {
  withAction?: (action: string) => SotiIntent;
  withData?: (data: string | SotiUri) => SotiIntent;
  withExtras?: (bundle: SotiBundle) => SotiIntent;
  withComponent?: (component: string) => SotiIntent;
  withType?: (mimeType: string) => SotiIntent;
  withCategories?: (categories: string[]) => SotiIntent;
  /** Activity flags. NOT shown in the intents tutorial — included
   *  best-effort for the NEW_TASK requirement and called defensively;
   *  pending on-device confirmation of the exact method. */
  withFlags?: (flags: number) => SotiIntent;
}

/** SOTI Bundle builder for intent extras (createBundle()). Typed methods
 *  per value kind, not a single putExtra. */
interface SotiBundle {
  withString?: (key: string, value: string) => SotiBundle;
  withBoolean?: (key: string, value: boolean) => SotiBundle;
  withInteger?: (key: string, value: number) => SotiBundle;
  withUri?: (key: string, value: SotiUri) => SotiBundle;
}

interface SotiUri {
  /** Marker for the opaque SOTI Uri object. */
  __sotiUri?: true;
}

interface SotiAndroid {
  createIntent?: () => SotiIntent;
  createBundle?: () => SotiBundle;
  createUri?: (uriString: string) => SotiUri;
  createFileContentUri?: (file: unknown) => SotiUri;
  startActivity?: (intent: SotiIntent) => void;
  startActivityForResult?: (
    intent: SotiIntent,
    callback: (result: unknown) => void,
    timeoutMs?: number,
  ) => void;
  sendBroadcast?: (intent: SotiIntent) => void;
  startService?: (intent: SotiIntent) => void;
}

/** A mobicontrol.io File handle. Obtained via mobicontrol.io.createFile /
 *  createDirectory. Methods are synchronous on the documented surface. */
interface SotiFile {
  path?: string;
  exists?: boolean;
  size?: number;
  lastModified?: number;
  isDirectory?: boolean;
  isNormalFile?: boolean;
  readText?: () => string;
  writeText?: (content: string) => void;
  createFile?: () => boolean | void;
  createDirectory?: () => boolean | void;
  delete?: () => boolean | void;
  listFiles?: () => SotiFile[] | null;
}

interface SotiIo {
  createFile?: (path: string) => SotiFile | null;
  createDirectory?: (path: string) => SotiFile | null;
}

interface SotiStorageArea {
  dataDirectory?: string | null;
  rootDirectory?: string | null;
  documentsDirectory?: string | null;
  downloadsDirectory?: string | null;
  availableSpace?: number | null;
  totalSpace?: number | null;
}

interface SotiStorage {
  internal?: SotiStorageArea | null;
  external?: SotiStorageArea | null;
}

interface SotiMemory {
  availableSpace?: number | null;
  totalSpace?: number | null;
}

interface SotiLog {
  debug?: (message: string) => void;
  info?: (message: string) => void;
  warn?: (message: string) => void;
  error?: (message: string) => void;
}

/** mobicontrol.sharedDevice.currentUser may be a bare username string or
 *  a richer object; we read both. */
interface SotiSharedUser {
  id?: string | null;
  name?: string | null;
  userName?: string | null;
  email?: string | null;
  emailAddress?: string | null;
}

interface SotiSharedDevice {
  currentUser?: SotiSharedUser | string | null;
  isUserLoggedIn?: () => boolean;
}

interface SotiRoot {
  app?: SotiApp;
  packages?: SotiPackages;
  device?: SotiDevice;
  battery?: SotiBattery;
  cellular?: SotiCellular;
  network?: SotiNetwork;
  agent?: SotiAgent;
  android?: SotiAndroid;
  io?: SotiIo;
  storage?: SotiStorage;
  memory?: SotiMemory;
  log?: SotiLog;
  sharedDevice?: SotiSharedDevice;
}

declare global {
  interface Window {
    /** SOTI MobiControl Android Agent JS API (v1.x). Injected by the
     *  Lockdown Profile's WebView at page load when the device is
     *  running MobiControl. */
    mobicontrol?: SotiRoot;
  }
}

/* Standard Android intent action / flag constants we need. */
const ACTION_VIEW = 'android.intent.action.VIEW';
const FLAG_ACTIVITY_NEW_TASK = 0x10000000;
/* Chrome tab-control intent extras. CREATE_NEW_TAB forces a fresh tab;
 * APPLICATION_ID keyed to a stable id makes Chrome reuse the tab it
 * previously opened for that id ("same tab"). */
const EXTRA_CREATE_NEW_TAB = 'create_new_tab';
const EXTRA_APPLICATION_ID = 'com.android.browser.application_id';
const LAUNCHER_APPLICATION_ID = 'com.oakash.mobilauncher';

/**
 * Boot-time probe. The agent-host selector calls this and only picks
 * SotiAgentHost when both this and `config.mdm === 'mobicontrol'`
 * are true.
 */
export function hasSotiAgent(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.mobicontrol === 'object' &&
    window.mobicontrol !== null
  );
}

function requireSoti(): SotiRoot {
  if (typeof window === 'undefined' || !window.mobicontrol) {
    throw new Error('SOTI MobiControl JS Agent API not available on this device');
  }
  return window.mobicontrol;
}

/* ───── Host ───── */

export class SotiAgentHost implements AgentHost {
  launchApp(packageName: string): Promise<void> {
    return invokeBool(() => {
      const m = requireSoti();
      if (!m.app?.start) throw new Error('mobicontrol.app.start not available');
      return m.app.start(packageName);
    }, `launchApp(${packageName}) returned false`);
  }

  openUrl(url: string, options?: { external?: boolean; tab?: 'same' | 'new' }): Promise<void> {
    return invokeVoid(() => {
      // Defense in depth: reject non-http(s) before it reaches
      // window.location.href or an Android intent.
      assertHttpUrl(url);
      if (options?.external !== true) {
        // In-launcher navigation: matches the other hosts' behavior
        // for non-external opens.
        window.location.href = url;
        return;
      }
      const m = requireSoti();
      if (!m.android?.createIntent || !m.android.startActivity) {
        // Bridge missing intent helpers — degrade to in-WebView nav.
        window.location.href = url;
        return;
      }
      // Fluent builder per the intents tutorial. withData accepts a plain
      // string URL (as the tutorial's content:// example shows); each
      // with* returns the intent, so we thread it through, falling back
      // to the prior handle if a given builder method is unavailable.
      let intent = m.android.createIntent();
      intent = intent.withAction?.(ACTION_VIEW) ?? intent;
      intent = intent.withData?.(url) ?? intent;
      // Tab control via intent extras (Bundle builder). 'same' reuses the
      // launcher's Chrome tab via a stable application id; 'new' (and
      // undefined) forces a fresh tab.
      const bundle = m.android.createBundle?.();
      if (bundle) {
        const filled =
          options.tab === 'same'
            ? bundle.withString?.(EXTRA_APPLICATION_ID, LAUNCHER_APPLICATION_ID)
            : bundle.withBoolean?.(EXTRA_CREATE_NEW_TAB, true);
        intent = intent.withExtras?.(filled ?? bundle) ?? intent;
      }
      // NEW_TASK is normally required to launch an activity from the
      // agent's non-activity context, but the intents tutorial documents
      // no flags method — best-effort, pending on-device confirmation.
      intent = intent.withFlags?.(FLAG_ACTIVITY_NEW_TASK) ?? intent;
      m.android.startActivity(intent);
    });
  }

  reboot(): Promise<void> {
    return invokeBool(() => {
      const m = requireSoti();
      if (!m.device?.reboot) throw new Error('mobicontrol.device.reboot not available');
      return m.device.reboot();
    }, 'reboot returned false');
  }

  clearAppData(packageName: string | undefined): Promise<void> {
    return invokeVoid(() => {
      if (packageName === undefined) {
        throw new Error('SOTI clearAppData requires an explicit targetPackage');
      }
      const m = requireSoti();
      if (!m.app?.clearData) {
        throw new Error('mobicontrol.app.clearData not available');
      }
      m.app.clearData(packageName);
    });
  }

  runScript(scriptRef: string): Promise<void> {
    // SOTI v1.10's public JS API doesn't expose script execution.
    // Equivalent server-side: push a scripted profile via the
    // MobiControl console. Reject explicitly so admins know.
    return Promise.reject(
      new Error(
        `runScript(${scriptRef}) is not supported by SOTI's public JS API; ` +
          'use a server-side scripted MobiControl profile.',
      ),
    );
  }

  syncNow(): Promise<void> {
    return invokeVoid(() => {
      const m = requireSoti();
      if (!m.agent?.checkIn) {
        throw new Error('mobicontrol.agent.checkIn not available');
      }
      m.agent.checkIn();
    });
  }

  /**
   * Pulls device identity, battery, network, cellular info from the
   * SOTI API and merges over browser-derived properties. Each accessor
   * is wrapped so a throwing method or a missing API surface doesn't
   * poison the whole dossier.
   */
  async getDeviceProperties(): Promise<DeviceProperties> {
    const browser = collectDeviceProperties();
    const storage = await getBrowserStorageInfo();
    const out: DeviceProperties = {
      formFactor: browser.formFactor,
      os: browser.os,
      browser: browser.browser,
      language: browser.language,
      timezone: browser.timezone,
      online: browser.online,
      screen: browser.screen,
    };
    if (browser.hardwareConcurrency !== undefined) {
      out.hardwareConcurrency = browser.hardwareConcurrency;
    }
    if (browser.deviceMemoryGb !== undefined) out.deviceMemoryGb = browser.deviceMemoryGb;
    if (storage) {
      out.storageUsageBytes = storage.usageBytes;
      out.storageQuotaBytes = storage.quotaBytes;
    }
    if (browser.connection) out.connection = browser.connection;

    const m = window.mobicontrol;
    if (!m) return out;

    /* device identity */
    const deviceName = readProp(() => m.device?.name);
    if (typeof deviceName === 'string' && deviceName.length > 0) out.deviceName = deviceName;
    const manufacturer = readProp(() => m.device?.manufacturer);
    if (typeof manufacturer === 'string' && manufacturer.length > 0) {
      out.manufacturer = manufacturer;
    }
    const model = readProp(() => m.device?.model);
    if (typeof model === 'string' && model.length > 0) out.model = model;
    const serial = readProp(() => m.device?.serialNumber);
    if (typeof serial === 'string' && serial.length > 0) out.serialNumber = serial;
    const mac = readProp(() => m.device?.macAddress);
    if (typeof mac === 'string' && mac.length > 0) out.macAddress = mac;

    /* cellular identity */
    const imei = readProp(() => m.cellular?.imei);
    if (typeof imei === 'string' && imei.length > 0) out.imei = imei;
    const carrier = readProp(() => m.cellular?.carrier);
    if (typeof carrier === 'string' && carrier.length > 0) out.carrier = carrier;
    const roaming = readProp(() => m.cellular?.isRoaming);
    if (typeof roaming === 'boolean') out.roaming = roaming;
    /* signal — cellular ASU converts to dBm via the standard GSM
     * formula: dBm = -113 + 2 * ASU. Only used when cellular is the
     * active transport (Wi-Fi signal lives elsewhere on SOTI). */
    const asu = readProp(() => m.cellular?.signalStrengthInAsu);

    /* network / transport */
    const ssid = readProp(() => m.network?.ssid);
    if (typeof ssid === 'string' && ssid.length > 0) out.ssid = ssid;
    const net = readProp(() => m.network?.activeNetwork);
    if (net) {
      if (net.hasVpnTransport === true) out.vpnConnected = true;
      else if (net.hasVpnTransport === false) out.vpnConnected = false;
      if (net.hasCellularTransport === true && typeof asu === 'number') {
        out.signalDbm = -113 + 2 * asu;
      }
      if (typeof net.downstreamBandwidthKbps === 'number') {
        out.downlinkKbps = net.downstreamBandwidthKbps;
      }
      if (typeof net.upstreamBandwidthKbps === 'number') {
        out.uplinkKbps = net.upstreamBandwidthKbps;
      }
      if (typeof net.isMetered === 'boolean') out.metered = net.isMetered;
    }

    /* battery */
    const batLevel = readProp(() => m.battery?.level);
    if (typeof batLevel === 'number') out.batteryLevel = batLevel;
    const batCharging = readProp(() => m.battery?.isCharging);
    if (typeof batCharging === 'boolean') out.batteryCharging = batCharging;
    const batTemp = readProp(() => m.battery?.temperature);
    if (typeof batTemp === 'number') out.batteryTemperatureC = batTemp;

    return out;
  }

  /**
   * Inventory installed apps. Reads `mobicontrol.app.installedApps`,
   * falling back to `mobicontrol.packages.installedApps` if that's where
   * a given agent build surfaces it. Entries without a packageName are
   * dropped rather than failing the whole call.
   */
  async getInstalledApps(): Promise<InstalledApp[]> {
    const m = requireSoti();
    const list = readProp(() => m.app?.installedApps) ?? readProp(() => m.packages?.installedApps);
    if (!Array.isArray(list)) {
      throw new Error('mobicontrol.app.installedApps not available');
    }
    return list
      .map(normalizeInstalledApp)
      .filter((app): app is InstalledApp => app !== null);
  }

  /** Internal/external storage + RAM usage from the agent. */
  async getStorageInfo(): Promise<StorageInfo> {
    const m = requireSoti();
    const out: StorageInfo = {};

    const internal = pickStorageArea(readProp(() => m.storage?.internal));
    if (internal) out.internal = internal;
    const external = pickStorageArea(readProp(() => m.storage?.external));
    if (external) out.external = external;

    const memAvail = readProp(() => m.memory?.availableSpace);
    const memTotal = readProp(() => m.memory?.totalSpace);
    const memory: NonNullable<StorageInfo['memory']> = {};
    if (typeof memAvail === 'number') memory.availableSpace = memAvail;
    if (typeof memTotal === 'number') memory.totalSpace = memTotal;
    if (Object.keys(memory).length > 0) out.memory = memory;

    return out;
  }

  /** Read a UTF-8 text file from the device filesystem. */
  async readFile(path: string): Promise<string> {
    const file = this.openFile(path);
    if (typeof file.readText !== 'function') {
      throw new Error('mobicontrol.io File.readText not available');
    }
    if (file.exists === false) {
      throw new Error(`file does not exist: ${path}`);
    }
    return file.readText();
  }

  /** Write a UTF-8 text file to the device filesystem, creating it if
   *  the agent reports it doesn't yet exist. */
  async writeFile(path: string, contents: string): Promise<void> {
    const file = this.openFile(path);
    if (typeof file.writeText !== 'function') {
      throw new Error('mobicontrol.io File.writeText not available');
    }
    if (file.exists === false && typeof file.createFile === 'function') {
      file.createFile();
    }
    file.writeText(contents);
  }

  /** List the entries of a directory on the device filesystem. */
  async listDir(path: string): Promise<FileEntry[]> {
    const dir = this.openFile(path);
    if (typeof dir.listFiles !== 'function') {
      throw new Error('mobicontrol.io File.listFiles not available');
    }
    const entries = dir.listFiles() ?? [];
    return entries.map((f) => {
      const entry: FileEntry = {
        path: typeof f.path === 'string' ? f.path : '',
        isDirectory: f.isDirectory === true,
      };
      if (typeof f.size === 'number') entry.size = f.size;
      if (typeof f.lastModified === 'number') entry.lastModified = f.lastModified;
      return entry;
    });
  }

  /**
   * Surface a log line to the MobiControl server via the agent.
   * Best-effort and synchronous: swallows everything (missing bridge,
   * missing level fn, thrown agent call) so callers never need to guard
   * a log statement.
   */
  log(level: LogLevel, message: string): void {
    try {
      const logger = window.mobicontrol?.log;
      if (!logger) return;
      const fn = logger[level] ?? logger.info;
      if (typeof fn === 'function') fn(message);
    } catch {
      // logging must never throw
    }
  }

  /** The signed-in shared-device user, or null if none / logged out. */
  async getCurrentUser(): Promise<DeviceUser | null> {
    const m = requireSoti();
    const sd = m.sharedDevice;
    if (!sd) throw new Error('mobicontrol.sharedDevice not available');
    if (typeof sd.isUserLoggedIn === 'function' && sd.isUserLoggedIn() === false) {
      return null;
    }
    const raw = readProp(() => sd.currentUser);
    if (raw === null || raw === undefined) return null;
    if (typeof raw === 'string') {
      return raw.length > 0 ? { name: raw } : null;
    }
    const out: DeviceUser = {};
    const name = raw.name ?? raw.userName;
    if (typeof name === 'string' && name.length > 0) out.name = name;
    if (typeof raw.id === 'string' && raw.id.length > 0) out.id = raw.id;
    const email = raw.email ?? raw.emailAddress;
    if (typeof email === 'string' && email.length > 0) out.email = email;
    return Object.keys(out).length > 0 ? out : null;
  }

  /**
   * Read an Android system property (e.g. `ro.product.manufacturer`).
   * Resolves undefined when the property is unset or empty.
   */
  async getSystemProperty(name: string): Promise<string | undefined> {
    const m = requireSoti();
    if (!m.device?.getSystemProperty) {
      throw new Error('mobicontrol.device.getSystemProperty not available');
    }
    const value = m.device.getSystemProperty(name);
    return typeof value === 'string' && value.length > 0 ? value : undefined;
  }

  /** Obtain a mobicontrol.io File handle for `path`, or throw if the io
   *  surface is unavailable. */
  private openFile(path: string): SotiFile {
    const m = requireSoti();
    if (!m.io?.createFile) {
      throw new Error('mobicontrol.io.createFile not available');
    }
    const file = m.io.createFile(path);
    if (!file) {
      throw new Error(`mobicontrol.io.createFile returned no handle for ${path}`);
    }
    return file;
  }
}

/* ───── Helpers ───── */

/** Wraps a void-returning SOTI call. Converts thrown errors to rejection. */
function invokeVoid(fn: () => void): Promise<void> {
  try {
    fn();
    return Promise.resolve();
  } catch (err) {
    return Promise.reject(err instanceof Error ? err : new Error(String(err)));
  }
}

/** Wraps a boolean-returning SOTI call. Rejects on `false` with the
 *  caller-supplied message. */
function invokeBool(fn: () => boolean, falseMsg: string): Promise<void> {
  try {
    const result = fn();
    if (result === false) return Promise.reject(new Error(falseMsg));
    return Promise.resolve();
  } catch (err) {
    return Promise.reject(err instanceof Error ? err : new Error(String(err)));
  }
}

/** Read a SOTI property/accessor; swallow throws so one bad accessor
 *  doesn't take down the whole getDeviceProperties() call. */
function readProp<T>(fn: () => T): T | undefined {
  try {
    return fn();
  } catch {
    return undefined;
  }
}

/** Map a raw SOTI installed-app entry to our InstalledApp, reading field
 *  aliases. Returns null when there's no usable packageName. */
function normalizeInstalledApp(raw: SotiInstalledApp): InstalledApp | null {
  const packageName = raw.packageName;
  if (typeof packageName !== 'string' || packageName.length === 0) return null;
  const out: InstalledApp = { packageName };
  const name = raw.name ?? raw.label;
  if (typeof name === 'string' && name.length > 0) out.name = name;
  const versionName = raw.versionName ?? raw.version;
  if (typeof versionName === 'string' && versionName.length > 0) out.versionName = versionName;
  if (typeof raw.versionCode === 'number') out.versionCode = raw.versionCode;
  const isSystem = raw.isSystemApp ?? raw.isSystem;
  if (typeof isSystem === 'boolean') out.isSystem = isSystem;
  return out;
}

/** Copy the defined fields of a SOTI storage area; returns undefined when
 *  the area is absent or contributes nothing. */
function pickStorageArea(
  area: SotiStorageArea | null | undefined,
): NonNullable<StorageInfo['external']> | undefined {
  if (!area) return undefined;
  const out: NonNullable<StorageInfo['external']> = {};
  if (typeof area.dataDirectory === 'string') out.dataDirectory = area.dataDirectory;
  if (typeof area.rootDirectory === 'string') out.rootDirectory = area.rootDirectory;
  if (typeof area.documentsDirectory === 'string') out.documentsDirectory = area.documentsDirectory;
  if (typeof area.downloadsDirectory === 'string') out.downloadsDirectory = area.downloadsDirectory;
  if (typeof area.availableSpace === 'number') out.availableSpace = area.availableSpace;
  if (typeof area.totalSpace === 'number') out.totalSpace = area.totalSpace;
  return Object.keys(out).length > 0 ? out : undefined;
}
